// NOTICE: KAPTION-INTEGRATION
// Kaption Feature Flags Configuration - Separate from Chatwoot's built-in feature flags
//
// This configuration is for the Kaption Feature Flags system which is:
// - External API-based (flags.kaptionai.com)
// - Cached in localStorage (persistent across sessions)
// - Refreshed every 30 seconds
// - NOT to be confused with Chatwoot's built-in feature flags
//

export const KAPTION_FEATURE_FLAGS_CONFIG = {
  // API Configuration
  //
  // NOTICE: KAPTION-INTEGRATION — VITE_KAPTION_FLAGS_URL points a LOCAL dev build at a
  // stand-in flags API (see features/kaption/smart-availability/evidence/mock-router.mjs),
  // which is what makes an evidence pass reproducible without hand-editing this file — the
  // previous pass did edit it, and the local URL nearly shipped. Unset everywhere else, so
  // staging and production can instead share their server-configured runtime URL.
  // Without either override, the original public endpoint remains the default.
  apiUrl:
    import.meta.env.VITE_KAPTION_FLAGS_URL ||
    window.chatwootConfig?.kaptionFeatureFlagsUrl ||
    'https://flags.kaptionai.com/platform/flags',
  timeout: 10000, // 10 seconds

  // Cache Configuration
  cacheTimeout: 30 * 60 * 1000, // 30 minutes in milliseconds
  // Development: refresh every 5 seconds, Production: every 30 seconds
  refreshInterval:
    import.meta.env.MODE === 'development' ? 5 * 1000 : 30 * 1000,

  // Installation Configuration
  installId: 'chat.kaptionai.com',

  // Service enabled flag
  enabled: true,

  // Fallback flags when API is unavailable
  // These should match the defaults in config/kaption_features.yml
  fallbackFlags: {
    KAPTION_FEATURE_FLAGS_DEBUG: false,
    KAPTION_CONVERSATION_TABS: false,
    KAPTION_AI_SUGGESTIONS: true,
    KAPTION_AI_SUGGESTIONS_USE_MOCK: false,
    KAPTION_DASHBOARD_APPS_JWT: true,
    KAPTION_PLATFORM_APP_AUTO_LINKING: true,
    KAPTION_LIVE_TRANSLATION: true,
    KAPTION_COPILOT: true,
    KAPTION_LANGUAGE_DETECTION_HEURISTICS: true,
    KAPTION_ENHANCED_CAMPAIGN_BUILDER: true,
    KAPTION_AI_STUDIO_SIDEBAR: false,
    KAPTION_CONCURRENT_CAMPAIGN_SENDING: true,
    KAPTION_CAMPAIGN_MOCK_WHATSAPP_API: false,
    KAPTION_DIRECT_SEND: false, // WhatsApp Direct Send API for single-recipient template sends
    KAPTION_CAMPAIGN_SKIP_ASSISTANT: false, // Deliver campaign replies straight to inbox, bypassing the assistant
    KAPTION_SIDEBAR_STARRING: true,
    KAPTION_NOTE_CARDS: true,
    KAPTION_UTILITY_TEMPLATE_REPLYBOX: true,
    KAPTION_SCHEDULED_MESSAGES: true,
    KAPTION_WABA_TEMPLATE_REGISTRY: true,
    KAPTION_AUTO_PROVISION_TEMPLATES: true,
    // desktop-notifications-nudge.FLAG.1 — default ON. This map is what the
    // dashboard actually reads when the flag is absent from the remote
    // response, so it is where default-on has to live for the UI; the Rails
    // fallback_flags entry covers the server side only.
    KAPTION_DESKTOP_NOTIFICATIONS_NUDGE: true,
    KAPTION_WHATSAPP_2FA: false, // Kaption2FA — WhatsApp second factor + Rumo MCP binding
    KAPTION_TWO_STEP_VERIFICATION: false, // two-step-verification.FLAG.1 — multi-method second step
    KAPTION_SECURITY_CENTER: false, // security-center.FLAG.1 — SSO, verified domains, session policy
    KAPTION_NOTIFICATION_SETTINGS_API: false, // notification-settings-api.FLAGS.1 — admin reads/changes a member's notification settings (API + Rumo MCP)
    KAPTION_WHATSAPP_TEMPLATE_MANAGEMENT: true, // Enabled for development
    KAPTION_PLATFORM_CHAT_LIST: true, // Enabled for development
    KAPTION_CONVERSATION_SEARCH: true, // Single-pane full-text conversation search
    KAPTION_SAVED_FILTERS: false, // "New filter" in the view switcher + header filter button (needs KAPTION_VIEW_SWITCHER; ships dark)
    KAPTION_VIEW_SWITCHER: false, // Chat list title as view switcher + quick filters + Chat preferences gear (ships dark)
    KAPTION_SHOW_SEND_SETTINGS: true, // Enabled for development (same as KAPTION_PLATFORM_CHAT_LIST)
    KAPTION_WHATSAPP_MARKDOWN_CONVERSION: true, // WhatsApp markdown conversion enabled by default
    KAPTION_CTWA_AD_BADGE: true, // ctwa-ad-header-badge.FLAG.1 — ad indicator in the chat header
    KAPTION_WIDGET_LAZY_REALTIME: false, // widget-lazy-realtime.FLAG.1 — web widget opens its socket lazily
    KAPTION_WIDGET_BOT_AVAILABILITY: false, // widget-bot-availability.FLAG.1 — an active assistant counts as available in the web widget
    KAPTION_ENHANCED_COMMAND_BAR: true,
    KAPTION_ACCESS_TOKENS: false,
    KAPTION_SALES_KANBAN: true, // Sales Funnel Kanban board view (enabled for development)
    KAPTION_SALES_PIPELINE: false, // Configurable sales pipeline (label stages, WIP, swimlanes)
    KAPTION_ACTION_PILL: false, // Action pill at bottom of chat list (privacy mode + scheduled messages)
    KAPTION_PRIVACY_MODE: false, // Privacy mode toggles (blur chat list + messages)
    KAPTION_VIDEO_DIRECT_URLS: true, // Direct S3/R2 URLs for video streaming (iOS AVPlayer support)
    KAPTION_POST_RESOLUTION_COOLDOWN: true, // Post-resolution cooldown for assistant webhooks
    KAPTION_GROUPS: true, // Group conversation support with multiple contacts
    KAPTION_RESPONSE_POLICIES: true, // Response time policy management
    KAPTION_DELIVERY_WEBHOOKS: true, // Delivery webhooks for campaign message status notifications
    KAPTION_MESSAGE_BATCHING: false, // Quantity-first WhatsApp outbound aggregation (opt-in)
    KAPTION_MESSAGE_MERGING: false, // whatsapp-message-merging: merge consecutive agent texts while typing (ships dark)
    KAPTION_AI_TAKEOVER: false, // ai-takeover: notes-only while the assistant works + one-click takeover (ships dark)
    KAPTION_AGENT_PROVIDERS: false, // Asistentes: one section for every assistant provider + per-inbox choice (ships dark)
    KAPTION_AGENT_PROVIDER_AGENTFORCE: false, // Asistentes: Salesforce Agentforce provider (needs KAPTION_AGENT_PROVIDERS; ships dark)
    KAPTION_AGENTFORCE_MANAGEMENT: false, // agentforce-firstclass: manage the Agentforce agent from Rumo (ships dark)
    KAPTION_INTERACTIVE_SETS: false, // Rumo sets: buttons/lists/cards library an agent can show (ships dark)
    KAPTION_SALESFORCE_CHANNEL: false, // salesforce-channel: inbox answered in the account's Salesforce, mirrored read-only (ships dark)
    KAPTION_AI_CONNECTORS: false, // ai-connectors: role-based AI connectors page + consent (ships dark)
    KAPTION_APP_STUDIO: false, // app-studio.PERMISSION.1 — Rumo Builder: App Studio launch + app placements (ships dark)
    KAPTION_CAMPAIGN_REPORTS: true, // Aggregate Campaign Reports dashboard
    KAPTION_USAGE_ANALYTICS: false, // usage-analytics.FLAG.1 — client-side product-usage telemetry (ships dark)
    KAPTION_WHATSAPP_BSUID_OBSERVE: false,
    KAPTION_WHATSAPP_BSUID_INBOUND_ALIASES: false,
    KAPTION_WHATSAPP_BSUID_CONVERSATION_REUSE: false,
    KAPTION_WHATSAPP_BSUID_COEXISTENCE_SYNC: false,
    KAPTION_WHATSAPP_BSUID_STATUS_ENRICHMENT: false,
    KAPTION_WHATSAPP_BSUID_OUTBOUND_RECIPIENT: false,
    KAPTION_WHATSAPP_BSUID_CAMPAIGNS: false,
    KAPTION_WHATSAPP_REQUEST_CONTACT_INFO: false,
    KAPTION_NEW_CONVERSATION_MODAL: false, // Template-first "New message" modal; opt-in per account
    KAPTION_REPLYBOX_TEMPLATE_PICKER: false, // reply-template-picker.FLAG.1 — "Plantilla" button in the reply box (ships dark)
    KAPTION_TEMPLATE_GALLERY_RUMO: false, // template-gallery-rumo.FLAG.1 — Rumo templates, favorites and one picker for Seguimiento (ships dark)
    KAPTION_CONTACT_FIRST_CONVERSATION: false, // contact-first-conversation.FLAG.1 — "Nuevo mensaje": contact first, then the conversation (ships dark)
    KAPTION_SMART_ASSIGNMENT: false, // smart-assignment.FLAG.1 — delegated assignment resolver (ships dark)
    KAPTION_EMBEDDED_CHANNEL: false, // Embedded channel (web, iOS, Android in one inbox); ships dark
    KAPTION_RUMO_THEME_VERDE_PAGO: false, // rumo-sdk-backend.THEME.1 — client-specific Studio preset
    KAPTION_RUMO_WEB_WIDGET_THEME: false, // rumo-web-widget.STUDIO.1 — Widget Studio for web widget inboxes
    KAPTION_HIDE_POWERED_BY: false, // rumo-rebrand.BRANDING.2 — hide the customer-facing "Powered by Rumo" footer per client
    KAPTION_RUMO_ACTIONS: false, // rumo-actions.PROTOCOL.1 — assistant reply buttons; agents see them read-only
    KAPTION_RUMO_NAVIGATION: false, // rumo-navigation.MESSAGE.1 — navigate/link buttons and start suggestions
    KAPTION_RUMO_CAROUSELS: false, // rumo-carousels.PROTOCOL.1 — card carousels on Embedded inboxes
    KAPTION_RUMO_SIGNED_SELECTIONS: true, // rumo-actions.SELECTION.3 — signed button taps
    KAPTION_QUOTED_REPLY_CONTEXT: false, // quoted-reply-context.FLAG.1 — quoted message in assistant webhooks
    // NOTICE: KAPTION-INTEGRATION - whatsapp-calling.FLAGS.1..4 (all ship dark)
    KAPTION_WHATSAPP_CALLING: false, // whatsapp-calling.FLAGS.1 — calls UI, softphone, Calls workspace, inbox Calling settings
    KAPTION_WHATSAPP_CALLING_OUTBOUND: false, // whatsapp-calling.FLAGS.2 — call button + call permission requests
    KAPTION_CALL_RECORDING: false, // whatsapp-calling.FLAGS.3 / whatsapp-call-transcripts.FLAGS.1 — record calls: player, dock Recording chip, inbox Recording settings
    KAPTION_CALL_TRANSCRIPTION: false, // whatsapp-call-transcripts.FLAGS.1 — transcript reader in the call detail + bubble
    KAPTION_CALL_TRANSCRIPT_SEARCH: false, // call-transcript-search.API.1 — search what was said (Calls workspace + global search)
    KAPTION_CALL_ASSISTANT: false, // whatsapp-calling-ai.FLAGS.1 — "Who answers calls" setting, AI legs, "Take the call"
    KAPTION_CALL_STUDIOCHAT_EVENTS: false, // whatsapp-calling-ai.FLAGS.2 — AI call summary card on the call
    KAPTION_WHATSAPP_FLOWS: false, // whatsapp-flows.FLAG.1 — Flows library, flow bubbles and composer picker, FLOW-button templates (ships dark)
  },
};

// NOTICE: KAPTION-INTEGRATION
// Flag definitions with metadata - mirrors config/kaption_features.yml
// This allows the debug screen to show all known flags with descriptions
export const KAPTION_FLAG_DEFINITIONS = {
  // Debug & Observability
  KAPTION_FEATURE_FLAGS_DEBUG: {
    description:
      'Enable the Kaption Feature Flags debug screen for inspecting flags',
    category: 'Debug',
    status: 'experimental',
    default: false,
  },

  // AI Features
  KAPTION_AI_SUGGESTIONS: {
    description:
      'Enable AI-powered response suggestions in conversation reply box',
    category: 'AI',
    status: 'experimental',
    default: false,
  },
  KAPTION_AI_SUGGESTIONS_USE_MOCK: {
    description:
      'Use mock data for AI suggestions instead of calling external API (for development)',
    category: 'AI',
    status: 'experimental',
    default: false,
  },

  // Dashboard Apps
  KAPTION_DASHBOARD_APPS_JWT: {
    description:
      'Enable JWT-based authentication for Dashboard Apps with minimal payload and 24-hour caching',
    category: 'Security',
    status: 'experimental',
    default: true,
  },

  // Platform Management
  KAPTION_PLATFORM_APP_AUTO_LINKING: {
    description:
      'Automatically grant Platform App permission to manage all newly created accounts',
    category: 'Dashboard',
    status: 'experimental',
    default: true,
  },

  // Translation
  KAPTION_LIVE_TRANSLATION: {
    description:
      'Enable live translation of messages between agents and customers',
    category: 'AI',
    status: 'experimental',
    default: true,
  },

  // Language Detection
  KAPTION_LANGUAGE_DETECTION_HEURISTICS: {
    description:
      'Enable heuristic-based language detection using phrase matching and phone country codes for faster detection',
    category: 'AI',
    status: 'stable',
    default: true,
  },

  // Campaigns
  KAPTION_ENHANCED_CAMPAIGN_BUILDER: {
    description:
      'Enable enhanced campaign builder with inline parameter binding and preview',
    category: 'Campaigns',
    status: 'experimental',
    default: true,
  },
  KAPTION_CONCURRENT_CAMPAIGN_SENDING: {
    description:
      'Enable concurrent message sending for WhatsApp campaigns with 60 MPS rate limiting and batch processing',
    category: 'Campaigns',
    status: 'experimental',
    default: true,
  },
  KAPTION_CAMPAIGN_MOCK_WHATSAPP_API: {
    description:
      'Use mock WhatsApp API for marketing campaigns (no real messages sent). For testing large campaigns with real data.',
    category: 'Campaigns',
    status: 'experimental',
    default: false,
  },
  KAPTION_DIRECT_SEND: {
    description:
      'Enable WhatsApp Direct Send API for sending template messages to individual phone numbers on demand',
    category: 'Campaigns',
    status: 'experimental',
    default: false,
  },
  KAPTION_CAMPAIGN_SKIP_ASSISTANT: {
    description:
      'Allow a campaign to deliver replies straight to the inbox (open + unassigned), bypassing the Kaption assistant',
    category: 'Campaigns',
    status: 'experimental',
    default: false,
  },

  // Kaption Copilot
  KAPTION_COPILOT: {
    description:
      'Enable Kaption Copilot in conversation sidebar for AI-powered agent assistance',
    category: 'AI',
    status: 'experimental',
    default: true,
  },

  // AI Studio
  KAPTION_AI_STUDIO_SIDEBAR: {
    description:
      'Enable the AI Studio sidebar with tree navigation inside the AI Studio iframe',
    category: 'AI Studio',
    status: 'experimental',
    default: false,
  },

  // UI Features
  KAPTION_SIDEBAR_STARRING: {
    description:
      'Enable per-user starring of sidebar items in conversations dropdown',
    category: 'UI',
    status: 'experimental',
    default: true,
  },
  KAPTION_NOTE_CARDS: {
    description:
      'Enable structured private-note cards rendered from markdown/text in conversation view',
    category: 'Conversations',
    status: 'stable',
    default: true,
  },

  // Utility Templates - ReplyBox
  KAPTION_UTILITY_TEMPLATE_REPLYBOX: {
    description:
      'Enable utility template features in ReplyBox (follow-up and scheduled messages)',
    category: 'Conversations',
    status: 'experimental',
    default: true,
  },

  // WABA Template Registry
  KAPTION_WABA_TEMPLATE_REGISTRY: {
    description:
      'Enable WABA-level template registry to prevent duplicate template creation across inboxes sharing the same WhatsApp Business Account',
    category: 'Templates',
    status: 'experimental',
    default: true,
  },

  // Auto-Provision Templates
  KAPTION_AUTO_PROVISION_TEMPLATES: {
    description:
      'Automatically create and sync Kaption utility templates when a new WhatsApp inbox is connected. Uses WABA registry to avoid duplicates.',
    category: 'Templates',
    status: 'experimental',
    default: true,
  },

  // WhatsApp Template Management
  KAPTION_WHATSAPP_TEMPLATE_MANAGEMENT: {
    description:
      'Enable WhatsApp template CRUD (list, create, sync) in the Campaigns > WhatsApp > Templates tab',
    category: 'Templates',
    status: 'experimental',
    default: false,
  },

  // Scheduled Messages
  KAPTION_SCHEDULED_MESSAGES: {
    description:
      'Enable scheduling messages for future delivery in Reply and Follow-up modes with timezone support',
    category: 'Conversations',
    status: 'experimental',
    default: true,
  },

  // Platform Chat List
  KAPTION_PLATFORM_CHAT_LIST: {
    description:
      'Enable the new Kaption Platform Chat List with enhanced topbar (cycling filters, status tabs, favorites, folders, labels, channels)',
    category: 'UI',
    status: 'experimental',
    default: true,
  },
  KAPTION_CONVERSATION_SEARCH: {
    description:
      'Enable single-pane full-text conversation search with contact, message, and audio transcription matches',
    category: 'UI',
    status: 'experimental',
    default: true,
  },
  KAPTION_SAVED_FILTERS: {
    description:
      'Create custom filters from the chat-list view switcher ("New filter" + header filter button) and save them as folders. Requires KAPTION_VIEW_SWITCHER',
    category: 'UI',
    status: 'experimental',
    default: false,
  },
  KAPTION_VIEW_SWITCHER: {
    description:
      'Chat list title becomes a searchable view switcher, with Unread/Mine/Unassigned chips, a People section (permission-gated), a status and sort menu, and a Chat preferences gear in the list header (replaces the cycling filter pill and category icons). Also hides the sidebar Inbox unless the sidebar_inbox permission allows it',
    category: 'UI',
    status: 'experimental',
    default: false,
  },

  // Settings
  KAPTION_SHOW_SEND_SETTINGS: {
    description:
      'Show Undo Mode and Enter to Send settings in the Kaption topbar settings panel',
    category: 'Settings',
    status: 'experimental',
    default: true,
  },

  // Enhanced Command Bar
  KAPTION_ENHANCED_COMMAND_BAR: {
    description:
      'Enable enhanced command bar with settings toggles, navigation filters, search, and quick actions',
    category: 'UI',
    status: 'experimental',
    default: true,
  },

  // Access Tokens
  KAPTION_ACCESS_TOKENS: {
    description:
      'Enable Kaption Access Tokens feature for creating scoped API tokens linked to Assistants',
    category: 'Security',
    status: 'experimental',
    default: false,
  },

  // WhatsApp Markdown Conversion
  KAPTION_WHATSAPP_MARKDOWN_CONVERSION: {
    description:
      'Enable automatic conversion of CommonMark markdown to WhatsApp-compatible formatting when sending messages. Converts bold (**text** → *text*), italic, strikethrough, code blocks, links, and mentions.',
    category: 'WhatsApp',
    status: 'stable',
    default: true,
  },

  // Click-to-WhatsApp ad indicator (ctwa-ad-header-badge.FLAG.1)
  // widget-bot-availability.FLAG.1
  KAPTION_WIDGET_BOT_AVAILABILITY: {
    description:
      'Web widget treats an inbox with an active agent bot (Studio Chat assistant) as available: "online" and "We reply instantly" instead of "We are away" (outside working hours only when the assistant handles messages outside hours).',
    category: 'Widget',
    status: 'experimental',
    default: false,
  },

  // widget-lazy-realtime.FLAG.1
  KAPTION_WIDGET_LAZY_REALTIME: {
    description:
      'Web widget opens its realtime socket only when the visitor opens the chat or has a recent conversation; closes it when the tab is hidden or the widget is idle, polling unread replies meanwhile.',
    category: 'Widget',
    status: 'experimental',
    default: false,
  },

  KAPTION_CTWA_AD_BADGE: {
    description:
      'Show a blue "Ad" badge in the conversation header, with the originating Click-to-WhatsApp ad creative (headline, text, image, link) in a popover.',
    category: 'WhatsApp',
    status: 'stable',
    default: true,
  },

  // Sales Funnel Kanban
  KAPTION_SALES_KANBAN: {
    description:
      'Enable Sales Funnel Kanban board view for conversations organized by deal pipeline stages',
    category: 'Sales',
    status: 'experimental',
    default: false,
  },
  KAPTION_SALES_PIPELINE: {
    description:
      'Configurable sales pipeline: admin-chosen label stages, WIP limits, collapsible Won/Lost columns, filters and swimlanes',
    category: 'Sales',
    status: 'experimental',
    default: false,
  },

  // Action Pill
  KAPTION_ACTION_PILL: {
    description:
      'Enable the KaptionActionPill at the bottom of the chat list with privacy mode and scheduled messages access',
    category: 'UI',
    status: 'experimental',
    default: false,
  },

  // Privacy Mode
  KAPTION_PRIVACY_MODE: {
    description:
      'Enable privacy mode toggles that blur conversation cards in the chat list and message bubbles inside conversations',
    category: 'UI',
    status: 'experimental',
    default: false,
  },

  // Post-Resolution Cooldown
  KAPTION_POST_RESOLUTION_COOLDOWN: {
    description:
      'Enable post-resolution cooldown for Kaption assistant webhooks per inbox (3, 6, or 12 hours)',
    category: 'Settings',
    status: 'experimental',
    default: true,
  },

  // Video Streaming
  KAPTION_VIDEO_DIRECT_URLS: {
    description:
      'Use direct S3/R2 URLs for video files instead of Rails proxy URLs. This enables proper Range request support for iOS AVPlayer streaming.',
    category: 'Storage',
    status: 'experimental',
    default: true,
  },

  // Group Conversations
  KAPTION_GROUPS: {
    description:
      'Enable group conversation support with multiple contacts per conversation. Includes group creation, contact management, and filtering.',
    category: 'Conversations',
    status: 'experimental',
    default: true,
  },

  // Response Policies
  KAPTION_RESPONSE_POLICIES: {
    description:
      'Enable response time policy management with threshold tracking for first response, next response, and resolution times',
    category: 'Settings',
    status: 'experimental',
    default: false,
  },

  // Delivery Webhooks
  KAPTION_DELIVERY_WEBHOOKS: {
    description:
      'Enable Delivery Webhooks for sending campaign message status notifications (sent, delivered, read, failed, replied) to client endpoints',
    category: 'Integrations',
    status: 'experimental',
    default: true,
  },

  KAPTION_MESSAGE_BATCHING: {
    description:
      'Enable quantity-first WhatsApp outbound aggregation delivery modes',
    category: 'Settings',
    status: 'experimental',
    default: false,
  },

  // whatsapp-message-merging (client-held merge on top of the Undo queue)
  KAPTION_MESSAGE_MERGING: {
    description:
      "Merge an agent's consecutive WhatsApp Cloud texts into one message while they keep typing (Inmediato / Equilibrado / Agrupar; Enter on an empty composer sends now)",
    category: 'Settings',
    status: 'experimental',
    default: false,
  },

  // ai-connectors
  KAPTION_AI_CONNECTORS: {
    description:
      'Settings → Integrations → AI connectors: connect Claude, ChatGPT, Notion and more by role and account, see exact permissions, manage your own connections',
    category: 'Settings',
    status: 'experimental',
    default: false,
  },

  // app-studio.PERMISSION.1
  KAPTION_APP_STUDIO: {
    description:
      'Rumo Builder: App Studio (hosted by the Builder, opened with a one-use launch token), the AI-connector App builder role, and Builder apps in the sidebar and conversation tabs',
    category: 'Settings',
    status: 'experimental',
    default: false,
  },

  // ai-takeover
  KAPTION_AI_TAKEOVER: {
    description:
      'While the Kaption assistant works a conversation, people write notes only and take it over with one click; the list shows and filters who is handling each conversation',
    category: 'Settings',
    status: 'experimental',
    default: false,
  },
  KAPTION_AGENT_PROVIDERS: {
    description:
      'Asistentes section: one place to choose which assistant (Studio Chat, Salesforce Agentforce) answers each inbox, replacing the AI Studio sidebar entry',
    category: 'AI',
    status: 'experimental',
    default: false,
  },
  KAPTION_AGENT_PROVIDER_AGENTFORCE: {
    description:
      'Salesforce Agentforce as an assistant provider: connection, read-only behavior and activity (requires KAPTION_AGENT_PROVIDERS)',
    category: 'AI',
    status: 'experimental',
    default: false,
  },
  KAPTION_AGENTFORCE_MANAGEMENT: {
    description:
      'Manage the Salesforce Agentforce agent from Rumo: instructions and subagents with versions, tools, tests and trusted domains (requires KAPTION_AGENT_PROVIDER_AGENTFORCE)',
    category: 'AI',
    status: 'experimental',
    default: false,
  },
  KAPTION_INTERACTIVE_SETS: {
    description:
      'Rumo sets: predefined buttons, lists and cards in Rumo that a Salesforce Agentforce agent can show (needs KAPTION_AGENT_PROVIDER_AGENTFORCE for delivery)',
    category: 'AI',
    status: 'experimental',
    default: false,
  },
  // NOTICE: KAPTION-INTEGRATION - salesforce-channel
  KAPTION_SALESFORCE_CHANNEL: {
    description:
      'Salesforce channel mode: customer messages go to the account Salesforce (Partner Messaging); Agentforce and reps answer there and Rumo mirrors the conversation read-only',
    category: 'AI',
    status: 'experimental',
    default: false,
  },

  // Product-Usage Analytics (usage-analytics.FLAG.1)
  KAPTION_USAGE_ANALYTICS: {
    description:
      'Enable client-side product-usage analytics (telemetry library + short-lived JWT mint). Metadata-only events; no end-customer PII.',
    category: 'Analytics',
    status: 'experimental',
    default: false,
  },

  KAPTION_WHATSAPP_BSUID_OBSERVE: {
    description: 'Emit redacted WhatsApp identity-shape telemetry only',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_INBOUND_ALIASES: {
    description: 'Resolve phone and BSUID aliases for inbound Cloud messages',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_CONVERSATION_REUSE: {
    description: 'Reuse conversations across aliases in one WhatsApp inbox',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_COEXISTENCE_SYNC: {
    description:
      'Enable BSUID-safe coexistence history and echo synchronization',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_STATUS_ENRICHMENT: {
    description: 'Enrich contacts from trusted BSUID status fields',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_OUTBOUND_RECIPIENT: {
    description: 'Use the Graph recipient field for one-to-one BSUID sends',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_CAMPAIGNS: {
    description: 'Allow verified BSUID aliases in WhatsApp campaigns',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_REQUEST_CONTACT_INFO: {
    description:
      'Enable the phone-number request action and Meta request_contact_info payload for eligible WhatsApp Cloud contacts',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },

  // Smart Assignment (smart-assignment.FLAG.1)
  KAPTION_SMART_ASSIGNMENT: {
    description:
      'Enable smart assignment: the account delegates "who should own this conversation" to an HTTP endpoint it configures. Gates the settings section, the command-bar entry, the manual endpoint and the automatic pass on team assignment.',
    category: 'Settings',
    status: 'experimental',
    default: false,
  },

  // New Conversation Modal (glass-tile, template-first)
  KAPTION_NEW_CONVERSATION_MODAL: {
    description:
      'Enable the always-visible top-bar "New message" button and the glass-tile, template-first new-conversation modal (contact/raw-number → template gallery + live preview → fill vars → send). Flag OFF leaves today\'s composer/entry points unchanged.',
    category: 'Conversations',
    status: 'experimental',
    default: false,
  },

  // Reply-box template picker (reply-template-picker.FLAG.1)
  KAPTION_REPLYBOX_TEMPLATE_PICKER: {
    description:
      'Enable the "Plantilla" button in the WhatsApp reply box: it opens the account\'s own templates for this inbox (search, filters, previews, pending, create), loads the picked one into the box with inline variables, and sends or schedules it through the existing follow-up and scheduling paths. Flag OFF leaves the reply box unchanged.',
    category: 'Conversations',
    status: 'experimental',
    default: false,
  },

  // Template gallery: Rumo templates + favorites (template-gallery-rumo.FLAG.1)
  KAPTION_TEMPLATE_GALLERY_RUMO: {
    description:
      'Template gallery ("Nuevo mensaje" and the reply-box "Plantilla" picker): adds a Rumo filter that lists the inbox\'s Rumo follow-up, reminder and scheduled templates in the active language, lets each agent star favorite templates (shown first), and makes Seguimiento in a closed window pick from the same gallery. Flag OFF leaves the gallery and reply box unchanged.',
    category: 'Conversations',
    status: 'experimental',
    default: false,
  },

  // Contact-first new conversation (contact-first-conversation.FLAG.1)
  KAPTION_CONTACT_FIRST_CONVERSATION: {
    description:
      'Enable the "Nuevo mensaje" button in the chat-list header: choose or create the contact, then open its conversation in the chosen WhatsApp inbox, or an empty one that is created on the first send. Requires KAPTION_REPLYBOX_TEMPLATE_PICKER. Flag OFF leaves the chat list unchanged.',
    category: 'Conversations',
    status: 'experimental',
    default: false,
  },
  KAPTION_EMBEDDED_CHANNEL: {
    description:
      'Enable the Embedded channel (chat inside a website or an iOS/Android app in one inbox), its native client API, push credentials, and Studio configuration.',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_RUMO_THEME_VERDE_PAGO: {
    description:
      'Offer the "Verde pago" theme preset in Widget Studio for this account',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_RUMO_WEB_WIDGET_THEME: {
    description:
      'Widget Studio for web widget inboxes (theme tokens, fonts, header and chip styles, live updates, voice messages)',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_HIDE_POWERED_BY: {
    description:
      'Hide the "Powered by Rumo" footer in the web widget, CSAT survey and help center for this account',
    category: 'Channels',
    status: 'stable',
    default: false,
  },
  KAPTION_RUMO_ACTIONS: {
    description:
      'Button protocol v1 for the assistant (reply buttons, list layout, suggestion chips) and taps as customer messages',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_RUMO_NAVIGATION: {
    description:
      'Navigation and link buttons from the Widget Studio destination catalogue, and start suggestions (needs KAPTION_RUMO_ACTIONS)',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_RUMO_CAROUSELS: {
    description:
      'Carousels of cards (image, title, subtitle, badge, navigate/link buttons) on Embedded inboxes, in compact, tall or list layout (actions need KAPTION_RUMO_NAVIGATION)',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_RUMO_SIGNED_SELECTIONS: {
    description:
      'Sign button taps (kaption_selection.signature) so Studiochat and webhooks can tell a real tap from typed text',
    category: 'Security',
    status: 'stable',
    default: true,
  },
  // NOTICE: KAPTION-INTEGRATION - whatsapp-calling.FLAGS.1..3
  KAPTION_WHATSAPP_CALLING: {
    description:
      'WhatsApp voice calls (Meta Cloud API Calling): incoming-call toast, floating softphone, Calls workspace and the inbox Calling settings',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_CALLING_OUTBOUND: {
    description:
      'Outbound WhatsApp calls from the conversation header, and call permission requests (needs KAPTION_WHATSAPP_CALLING)',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_CALL_RECORDING: {
    description:
      'Record WhatsApp calls: the recording player in the call detail, the Recording chip and notice in the call window, and the inbox Recording settings (needs KAPTION_WHATSAPP_CALLING)',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  // NOTICE: KAPTION-INTEGRATION - whatsapp-call-transcripts.FLAGS.1, call-transcript-search.API.1
  KAPTION_CALL_TRANSCRIPTION: {
    description:
      'Transcripts of recorded WhatsApp calls in the call detail and the conversation (needs KAPTION_CALL_RECORDING)',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_CALL_TRANSCRIPT_SEARCH: {
    description:
      'Search what was said in call transcripts, in the Calls workspace and the global search (needs KAPTION_CALL_TRANSCRIPTION)',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  // NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ai.FLAGS.1, .2
  KAPTION_CALL_ASSISTANT: {
    description:
      'The Studio Chat AI answers WhatsApp calls ("AI first" or "AI when nobody can") and hands them to people',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_CALL_STUDIOCHAT_EVENTS: {
    description:
      'Send ended WhatsApp calls to Studio Chat and show the AI call summary',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
  KAPTION_QUOTED_REPLY_CONTEXT: {
    description:
      'Send the quoted message (sender type, time, capped text) to the Kaption assistant when a customer replies by quoting. Never private notes or other conversations',
    category: 'Channels',
    status: 'experimental',
    default: false,
  },
};
